import { useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';
import { storage } from '../utils/localStorage';
import { getTenantIdFromToken } from '../utils/jwt';

const MERCURE_HUB_URL = process.env.REACT_APP_MERCURE_HUB_URL || 'MERCURE_HUB_URL_NOT_SET';


export interface MercureEvent {
  action: 'created' | 'updated' | 'deleted';
  screen: {
    id: string;
  };
  record: {
    id: number;
    data?: any;
  };
  user?: string;
  timestamp: number;
}

interface UseMercureOptions {
  screenId: string;
  enabled?: boolean;
  onEvent?: (event: MercureEvent) => void;
}

export const useMercure = ({ screenId, enabled = true, onEvent }: UseMercureOptions) => {
  const eventSourceRef = useRef<EventSource | null>(null);

  const connect = useCallback(async (forceRefreshToken = false) => {
    if (!enabled || !screenId) {
      return;
    }

    try {
      // Získaj JWT token a tenantId
      const jwtToken = storage.getItem('token');
      if (!jwtToken) {
        console.warn('[Mercure] No JWT token found');
        return;
      }

      const tenantId = getTenantIdFromToken(jwtToken);
      if (!tenantId) {
        console.warn('[Mercure] Could not extract tenant ID from token');
        return;
      }

      // Použij uložený Mercure token z localStorage
      let mercureToken = storage.getItem('mercureToken');

      // Získaj nový token len ak neexistuje alebo je forced refresh
      if (!mercureToken || forceRefreshToken) {
        const response = await apiService.getMercureToken();
        if (!response.success || !response.data.token) {
          return;
        }
        mercureToken = response.data.token;
        storage.setItem('mercureToken', mercureToken);
      }

      // Pripoj sa na Mercure hub
      const topic = `/tenant/${tenantId}/screen/${screenId}`;
      const url = new URL(MERCURE_HUB_URL);
      url.searchParams.append('topic', topic);
      // DÔLEŽITÉ: Mercure token sa posiela cez URL parameter, nie cez header!
      url.searchParams.append('authorization', mercureToken);

      const eventSource = new EventSource(url.toString(), {
        withCredentials: false,
      });

      eventSource.onopen = () => {
        console.log(`[Mercure] Connected to ${topic}`);
      };

      eventSource.onmessage = (event) => {
        try {
          const data: MercureEvent = JSON.parse(event.data);

          if (onEvent) {
            onEvent(data);
          }
        } catch (error) {
          console.error('[Mercure] Failed to parse event:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('[Mercure] Connection error:', error);
        eventSource.close();
        eventSourceRef.current = null;

        // Automatický reconnect po 5 sekundách s novým tokenom
        setTimeout(() => {
          connect(true); // Force refresh token
        }, 5000);
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      console.error('[Mercure] Failed to connect:', error);
    }
  }, [screenId, enabled, onEvent]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    connect,
    disconnect,
    isConnected: !!eventSourceRef.current,
  };
};
